// ADR 0023 Sprint 7 Wave B — Platform management layout wrapper.
//
// Wraps all /platform/* routes. Provides:
//   1. <TenantSelectorBand> — the tenant switcher header band (inline variant).
//   2. <TenantDetailBreadcrumb> — breadcrumb shown on all /platform/tenants/:publicId/*
//      sub-pages. Reads tenant name from TanStack Query cache (sub-pages populate
//      the cache themselves; this component never fires an extra request). Shows:
//        Tenants  /  {tenant name or publicId}  /  {section label}
//      where "Tenants" links to /platform (list) and the tenant name links to
//      /platform/tenants/:publicId (profile/home). Renders null on the index and
//      /tenants/new pages.
//   3. <Outlet> — renders the matched child route.
//
// This component is mounted as a nested layout route by the router:
//   {
//     path: '/platform',
//     element: <RequirePlatformAdminRoute />,  // gates on Me.is_platform_admin
//     children: [
//       { element: <PlatformLayout />, children: [...child routes] }
//     ]
//   }
//
// Phase 3 (tenant-view switcher): PlatformTenantProvider is now promoted to the
// AppShell level (wraps ALL authenticated routes). This layout no longer creates
// its own nested provider — it uses the outer provider from context. Removing the
// inner provider prevents the double-provider bug where the inner context's
// useEffect would race the outer context's useEffect on mount, causing
// inconsistent store state between the two provider instances.
//
// The outer RequirePlatformAdminRoute in the router handles the 403 case.
// TenantSelectorBand has its own useIsPlatformAdmin guard for belt-and-suspenders.
//
// Deduplication: TenantSelectorBand here is rendered with showExit={false} because
// AppShell already renders the global amber banner (variant='global') that provides
// the "Exit tenant view" and "Switch" buttons when a tenant is selected. The inline
// band is kept for the "no tenant selected" prompt and to show the active tenant
// name in the platform-section context, but its Exit button is suppressed to avoid
// duplicate controls. The Switch button is also hidden here because the global band
// provides it when a tenant is active; for selecting a new tenant (no-tenant state)
// the inline band's Switch remains the entry point but if no tenant is selected the
// global band is invisible so the Switch button must stay in the inline band.
//
// Summary of exit/switch control locations:
//   - Global amber band (AppShell, variant='global'): Exit + Switch — visible ONLY
//     when a tenant is selected. This is the ONE unified exit/switch affordance.
//   - Inline band (PlatformLayout, showExit=false): Switch only (no Exit) — always
//     visible on platform pages. Provides the "no tenant selected" prompt and tenant
//     name display. Switch allows changing tenant from any platform page.
import { Outlet, useParams, useLocation, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight, LayoutList } from 'lucide-react'
import { TenantSelectorBand } from './TenantSelectorBand'
import { getTenant } from '../api'

// ---- Section label map -------------------------------------------------------
// Maps the last URL segment of tenant sub-pages to a human-readable label.
// Used by TenantDetailBreadcrumb to display the current section name.
const SECTION_LABELS: Record<string, string> = {
  banking: 'Banking',
  fraud: 'Fraud',
  aml: 'AML',
  'case-workflow': 'Cases',
  notification: 'Notifications',
  'reporting-retention': 'Reporting',
  domains: 'Domains',
  ingestion: 'Ingestion',
  connectors: 'Connectors',
  mappings: 'Mappings',
  'maker-checker': 'Approvals',
  'maker-checker-policy': 'Approval Policy',
  users: 'Users',
  entitlements: 'Entitlements',
  checklist: 'Checklist',
  audit: 'Audit',
}

// ---- TenantDetailBreadcrumb -------------------------------------------------
//
// Shown on /platform/tenants/:publicId/* sub-routes. Subscribes to the same
// TanStack Query key used by every sub-page (['platform','tenant',publicId])
// with staleTime:Infinity so it reads from cache when available and triggers
// a background fetch only on a cold load. Falls back to the raw publicId while
// the cache is warming.
//
// NOT shown on:
//   /platform              (index — tenant list)
//   /platform/tenants/:publicId  (the tenant home/profile itself)
//   /platform/tenants/new        (create form)
function TenantDetailBreadcrumb() {
  const { publicId } = useParams<{ publicId?: string }>()
  const location = useLocation()

  // Derive the sub-page segment from the pathname.
  // Path shape: /platform/tenants/:publicId/<segment>
  // segments[0]='' [1]='platform' [2]='tenants' [3]=publicId [4]=section
  const segments = location.pathname.split('/')
  const section = segments[4] ?? ''

  // Subscribe to the tenant query so the breadcrumb updates reactively when
  // the sub-page's own query resolves. staleTime:Infinity prevents a duplicate
  // fetch — the sub-page is already managing freshness.
  const tenantQuery = useQuery({
    queryKey: ['platform', 'tenant', publicId],
    queryFn: () => {
      if (!publicId) throw new Error('publicId required')
      return getTenant(publicId)
    },
    enabled: !!publicId && !!section && publicId !== 'new',
    staleTime: Infinity,
  })

  // Only render on routes that have a sub-section (not the profile page itself)
  if (!publicId || !section || publicId === 'new') return null

  const tenantName =
    tenantQuery.data?.profile?.display_name ??
    tenantQuery.data?.name ??
    publicId

  const sectionLabel = SECTION_LABELS[section] ?? section

  return (
    <nav
      aria-label="Tenant breadcrumb"
      className="flex items-center gap-1 border-b border-border bg-muted/20 px-6 py-2 text-xs text-muted-fg"
    >
      <Link
        to="/platform"
        className="flex items-center gap-1 rounded px-1 py-0.5 hover:bg-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <LayoutList size={11} aria-hidden />
        Tenants
      </Link>
      <ChevronRight size={11} aria-hidden className="shrink-0" />
      <Link
        to={`/platform/tenants/${publicId}`}
        aria-label={`Back to ${tenantName}`}
        className="max-w-[16rem] truncate rounded px-1 py-0.5 font-medium text-fg hover:bg-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {tenantName}
      </Link>
      <ChevronRight size={11} aria-hidden className="shrink-0" />
      <span aria-current="page" className="px-1 py-0.5">
        {sectionLabel}
      </span>
    </nav>
  )
}

export function PlatformLayout() {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* showExit={false}: the global amber band in AppShell already provides
          "Exit tenant view" and "Switch" when a tenant is selected. Suppressing
          Exit here removes the duplicate control on /platform/* pages. */}
      <TenantSelectorBand showExit={false} />
      <TenantDetailBreadcrumb />
      <div className="flex-1 overflow-auto p-6">
        <Outlet />
      </div>
    </div>
  )
}
